/**
 * Chats, Easy-stand (02-10-2026, Jordy) — drie kolommen naast elkaar, zoals
 * de multi-chat van FanCentro:
 *
 *   links    de gesprekken, met de filters Open / My chats / Spenders / Timewasters
 *            (18-10-2026: My chats = de eigen box; de volgers-inbox is weg)
 *   midden   het open gesprek (hetzelfde chatvenster als /conversations/show)
 *   rechts   de modellen; klik er één om links alleen dat model te zien
 *
 * Bronnen, allemaal dezelfde als in de Pro-stand:
 *   - de wachtrij-query (`queue_candidates=true&panel=unanswered`), verdeeld
 *     door `bucketPanels` in lib/panels.ts. Open = buckets.open, Spenders =
 *     daarvan wie al iets uitgaf, Timewasters = buckets.timewasters;
 *   - /models/personas voor de modellen (voor een chatter al beperkt tot zijn
 *     eigen modellen), via lib/modelTegels.ts.
 *
 * Het open gesprek staat in het adres (`/conversations?chat=123`), zodat
 * herladen het gesprek openhoudt. Er gaat nooit vanzelf een gesprek open:
 * openen telt als gelezen en haalt het uit de Waiting queue.
 */
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErrorNote, Loading, Seg } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { bucketPanels } from '../lib/panels'
import type { QueueRow } from '../lib/panels'
import { avatarZoeker, modelTegels } from '../lib/modelTegels'
import { isBeheer, magPaneel, useMij } from '../lib/rechten'
import { GesprekLijst } from '../components/GesprekLijst'
import type { GesprekRij } from '../components/GesprekLijst'
import { ModelLijst } from '../components/ModelLijst'
import { ChatView } from './ChatWindow'
import type { Persona } from './Models'

type Filter = 'open' | 'mine' | 'spenders' | 'timewasters'

/** Precies de query van de Pro-stand; één bron voor beide standen. */
const WACHTRIJ =
  '/api/admin/conversations?limit=350&offset=0&skip_total=true' +
  '&queue_candidates=true&sort_order=desc&panel=unanswered&dashboard_blocked=false'

const LEEG: Record<Filter, string> = {
  open: 'No open chats.',
  spenders: 'No spenders waiting.',
  timewasters: 'No maybe timewasters waiting.',
  mine: 'No chats of yours right now.',
}

export default function ChatsEasy({ wissel }: { wissel: ReactNode }) {
  const [zoek, setZoek] = useSearchParams()
  const chatId = Number(zoek.get('chat') || 0) || null
  const [filter, setFilter] = useState<Filter>('open')
  /** De gekozen model-slug, of 'all'. */
  const [model, setModel] = useState('all')
  // 11-10-2026 (Jordy): het fan-paneel staat naast de chat en is bij elk
  // geopend gesprek zichtbaar; dat regelt ChatView zelf (verbergen geldt
  // alleen voor dat gesprek). De oude keuze `chats.easy.fanOpen` in
  // localStorage wordt niet meer gelezen.
  /**
   * Of het open gesprek uit de wachtrij kwam (voor de mark-read-vlag). Alleen
   * in het geheugen, niet in het adres: na herladen is het gesprek al eens
   * geopend en hoort de vlag er niet meer bij.
   */
  const [herkomst, setHerkomst] = useState<{ id: number; from: string } | null>(null)
  const mij = useMij().data

  const queue = useApi<{ data: GesprekRij[] }>(WACHTRIJ)
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  // Zelfde ritme als de Pro-stand.
  usePoll(5000, queue.refetch)
  usePoll(60000, personas.refetch)

  const buckets = useMemo(
    // 18-10-2026 (Jordy): owner/admin zien in My chats de claims van alle chatters.
    () => bucketPanels((queue.data?.data ?? []) as QueueRow[], null, isBeheer(mij)),
    [queue.data, mij],
  )
  const tegels = useMemo(() => modelTegels(personas.data), [personas.data])
  const avatarOf = useMemo(() => avatarZoeker(personas.data), [personas.data])

  // Filters die dit account niet mag zien, verdwijnen (panel_deny_*).
  const magSpenders = magPaneel(mij, 'spenders')
  const magTimewasters = magPaneel(mij, 'timewasters')
  const actief: Filter =
    (filter === 'spenders' && !magSpenders) || (filter === 'timewasters' && !magTimewasters)
      ? 'open' : filter

  const perFilter = useMemo(() => {
    const open = buckets.open as GesprekRij[]
    return {
      open,
      spenders: open.filter((c) => Number(c.fan_total_spend || 0) > 0),
      timewasters: buckets.timewasters as GesprekRij[],
      mine: buckets.mine as GesprekRij[],
    }
  }, [buckets])

  const inFilter = perFilter[actief]
  const rows = useMemo(
    () => (model === 'all' ? inFilter : inFilter.filter((c) => String(c.model_slug ?? '') === model)),
    [inFilter, model],
  )
  // 03-10-2026 (Jordy): met een model gekozen toont "Open" ook de al
  // beantwoorde chats van dat model, onder de wachtende (nieuwste activiteit
  // eerst). Alleen opgehaald zolang er een model gekozen is.
  const modelChats = useApi<{ data: GesprekRij[] }>(
    model !== 'all' && actief === 'open'
      ? '/api/admin/conversations?limit=100&offset=0&skip_total=true&sort_by=recent_activity'
        + `&sort_order=desc&dashboard_blocked=false&model_slug=${encodeURIComponent(model)}`
      : null,
    [model, actief],
  )
  usePoll(15000, () => { if (model !== 'all' && actief === 'open') modelChats.refetch() })
  const beantwoord = useMemo(() => {
    if (model === 'all' || actief !== 'open') return []
    const wacht = new Set(rows.map((c) => c.id))
    return (modelChats.data?.data ?? []).filter((c) => !wacht.has(c.id))
  }, [model, actief, rows, modelChats.data])
  const aantallen = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of inFilter) {
      const slug = String(c.model_slug ?? '')
      m.set(slug, (m.get(slug) ?? 0) + 1)
    }
    return m
  }, [inFilter])

  const filterOpties = [
    { value: 'open', label: `Open (${perFilter.open.length})` },
    { value: 'mine', label: `My chats (${perFilter.mine.length})` },
    ...(magSpenders ? [{ value: 'spenders', label: `Spenders (${perFilter.spenders.length})` }] : []),
    ...(magTimewasters
      ? [{ value: 'timewasters', label: `Maybe timewasters (${perFilter.timewasters.length})` }] : []),
  ]

  /** Een gesprek openen: in het adres, zodat herladen het openhoudt. */
  const open = (id: number, from: string | null) => {
    setHerkomst(from ? { id, from } : null)
    setZoek((z) => {
      const n = new URLSearchParams(z)
      n.set('chat', String(id))
      return n
    })
  }
  const sluit = () => {
    setHerkomst(null)
    setZoek((z) => {
      const n = new URLSearchParams(z)
      n.delete('chat')
      return n
    })
  }
  /**
   * Zoals de Pro-stand: een rij uit de Waiting queue of uit Spenders opent
   * met `from=waiting` (ChatPanels.tsx, WaitingPanel en SpendersPanel). Een
   * al geopende chat (Unanswered) en een timewaster openen zonder vlag.
   */
  const kies = (c: GesprekRij) => {
    const uitWachtrij = buckets.waiting.includes(c) || buckets.spenders.includes(c)
    open(c.id, uitWachtrij ? 'waiting' : null)
  }

  const modelKeuze = (
    <select className="select easymodelkeuze" value={model}
            onChange={(e) => setModel(e.target.value)} aria-label="Model">
      <option value="all">All models</option>
      {tegels.map((t) => <option key={t.key} value={t.slug}>{t.name}</option>)}
    </select>
  )

  return (
    <div className="easywrap">
      <div className="filters easykop">
        <Seg options={filterOpties} value={actief} onChange={(v) => setFilter(v as Filter)} />
        {modelKeuze}
        <span className="spacer" style={{ flex: 1 }} />
        {wissel}
      </div>
      <div className="easy3">
        <div className="panel easykolom easylinks">
          {queue.loading && !queue.data ? <Loading />
            : queue.error ? <ErrorNote error={queue.error} />
            : (
              <GesprekLijst rows={rows} gekozen={chatId} onKies={kies} avatarOf={avatarOf}
                            leeg={LEEG[actief]} beantwoord={beantwoord} />
            )}
        </div>
        <div className="easymidden">
          {chatId ? (
            <ChatView
              key={chatId}
              id={chatId}
              embedded
              from={herkomst?.id === chatId ? herkomst.from : null}
              onClose={sluit}
              onOpen={(id) => open(id, null)}
            />
          ) : (
            <div className="panel easykolom easyleeg midden">
              Select a chat on the left to open it here.
            </div>
          )}
        </div>
        <div className="panel easykolom easyrechts">
          <div className="easykoptekst">Models</div>
          <ModelLijst tegels={tegels} aantallen={aantallen} totaal={inFilter.length}
                      gekozen={model} onKies={setModel} />
        </div>
      </div>
    </div>
  )
}
